import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO, differenceInHours } from 'date-fns';
import {
  Alert,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  AddLocation as AddLocationIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Computer as ComputerIcon,
  Delete as DeleteIcon,
  DriveFileMove as DriveFileMoveIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  LocationOn as LocationIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  PointOfSale as PointOfSaleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Sync as SyncIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import LaborTargetsTab from '../tabs/LaborTargetsTab';
import { historyService, locationService } from '../services/domain'; // UPDATED: Using domain services
import { useUser } from 'components/RDAuthContext';
import EditableSection from './EditableSection';
import BasicInformationSection from './BasicInformationSection';
import OrganizationalAssignmentSection from './OrganizationalAssignmentSection';
import OperationalStatusSection from './OperationalStatusSection';
import EditLocationManagersModal from './EditLocationManagersModal';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function LocationDetailPanel({ location, open, onClose, onUpdate, loading = false, hierarchyDefinition = [], orgUnits = [] }) {
  const { user } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const [showInactiveSystems, setShowInactiveSystems] = useState(false);
  const [locationHistory, setLocationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editManagersModalOpen, setEditManagersModalOpen] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  
  // Track dirty state for each section
  const [dirtyStates, setDirtyStates] = useState({
    basicInfo: false,
    orgAssignment: false,
    operationalStatus: false
  });

  // Memoize dirty change handlers to prevent re-renders
  const handleBasicInfoDirtyChange = useCallback((isDirty) => {
    setDirtyStates(prev => ({ ...prev, basicInfo: isDirty }));
  }, []);

  const handleOrgAssignmentDirtyChange = useCallback((isDirty) => {
    setDirtyStates(prev => ({ ...prev, orgAssignment: isDirty }));
  }, []);

  const handleOperationalStatusDirtyChange = useCallback((isDirty) => {
    setDirtyStates(prev => ({ ...prev, operationalStatus: isDirty }));
  }, []);

  // Fetch location history when location changes or History tab is selected
  useEffect(() => {
    const fetchLocationHistory = async () => {
      if (!location?.id || tabValue !== 4) return;
      
      const locationId = location.id || location.locationId || location.ID;
      const clientId = user?.clientId || user?.client;
      
      if (!locationId || !clientId) {
        return;
      }
      
      setLoadingHistory(true);
      try {
        const history = await historyService.getLocationHistory(locationId, clientId);
        setLocationHistory(history || []);
      } catch (error) {
        console.error('Error fetching location history:', error);
        setLocationHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchLocationHistory();
  }, [location?.id, tabValue, user?.clientId, user?.client]);

  // Reset dirty states when location changes
  useEffect(() => {
    setDirtyStates({
      basicInfo: false,
      orgAssignment: false,
      operationalStatus: false
    });
  }, [location?.id]);

  if (!location) return null;

  // Check if any section has unsaved changes
  const hasUnsavedChanges = () => {
    return Object.values(dirtyStates).some(isDirty => isDirty);
  };

  // Handle closing the panel with unsaved changes check
  const handleClose = () => {
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    
    // Reset dirty states when closing
    setDirtyStates({
      basicInfo: false,
      orgAssignment: false,
      operationalStatus: false
    });
    
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    // Check for unsaved changes when switching tabs
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm('You have unsaved changes in the current tab. Are you sure you want to switch tabs?');
      if (!confirmed) return;
    }
    
    // Reset dirty states when switching tabs
    setDirtyStates({
      basicInfo: false,
      orgAssignment: false,
      operationalStatus: false
    });
    
    setTabValue(newValue);
  };

  // Handle saving basic information
  const handleSaveBasicInfo = async (data) => {
    try {
      const result = await locationService.updateLocationBasicInfo(
        location.id,
        data,
        user.userName || user.id,
        `${user.firstName} ${user.lastName}` || user.userName
      );
      
      // Update parent component
      if (onUpdate) {
        onUpdate(result);
      }
      
      // Clear dirty state for this section
      setDirtyStates(prev => ({ ...prev, basicInfo: false }));
      
      setSnackbar({
        open: true,
        message: 'Basic information updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving basic info:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update basic information',
        severity: 'error'
      });
      throw error;
    }
  };

  // Handle saving organizational assignment
  const handleSaveOrgAssignment = async (data) => {
    try {
      const result = await locationService.updateLocationOrgAssignment(
        location.id,
        data,
        user.userName || user.id,
        `${user.firstName} ${user.lastName}` || user.userName,
        user.clientId || user.client,
        orgUnits  // Pass the orgUnits for name lookup
      );
      
      // Update parent component
      if (onUpdate) {
        onUpdate(result);
      }
      
      // Clear dirty state for this section
      setDirtyStates(prev => ({ ...prev, orgAssignment: false }));
      
      setSnackbar({
        open: true,
        message: 'Organizational assignment updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving org assignment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update organizational assignment',
        severity: 'error'
      });
      throw error;
    }
  };

  // Handle saving operational status
  const handleSaveOperationalStatus = async (data) => {
    try {
      const result = await locationService.updateLocationOperationalStatus(
        location.id,
        data,
        user.userName || user.id,
        `${user.firstName} ${user.lastName}` || user.userName,
        user.clientId || user.client
      );
      
      // Update parent component
      if (onUpdate) {
        onUpdate(result);
      }
      
      // Clear dirty state for this section
      setDirtyStates(prev => ({ ...prev, operationalStatus: false }));
      
      setSnackbar({
        open: true,
        message: 'Operational status updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving operational status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update operational status',
        severity: 'error'
      });
      throw error;
    }
  };

  // Handle saving manager assignments
  const handleSaveManagers = async (managers) => {
    try {
      // Refresh location data to get updated manager assignments
      if (onUpdate && location?.id) {
        const updatedLocation = await locationService.getLocationFullDetails(
          user.clientId || user.client,
          location.id
        );
        
        // Update the parent component with the refreshed location data
        onUpdate(updatedLocation);
        
        // Also update the local location data if needed
        // This ensures the Management Tab shows the updated managers immediately
        if (updatedLocation) {
          // Force a re-render by updating the location in the parent
          onUpdate({ ...updatedLocation });
        }
        
        // If we're on the History tab, refresh the history to show the new audit log
        if (tabValue === 4) {
          const locationId = location.id || location.locationId || location.ID;
          const clientId = user?.clientId || user?.client;
          
          setLoadingHistory(true);
          try {
            const history = await historyService.getLocationHistory(locationId, clientId);
            setLocationHistory(history || []);
          } catch (error) {
            console.error('Error fetching location history after manager update:', error);
          } finally {
            setLoadingHistory(false);
          }
        }
      }
      
      setSnackbar({
        open: true,
        message: 'Manager assignments updated successfully',
        severity: 'success'
      });
      
      setEditManagersModalOpen(false);
    } catch (error) {
      console.error('Error in handleSaveManagers:', error);
      setSnackbar({
        open: true,
        message: 'Manager assignments saved but failed to refresh display. Please close and reopen to see changes.',
        severity: 'warning'
      });
      
      setEditManagersModalOpen(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { 
          width: { xs: '100%', sm: 480, md: 540 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'background.paper'
      }}>
        <Box display="flex" alignItems="center">
          <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight="600">
              {location.locationName}
            </Typography>
            {(location.locationNumber || location.city || location.state) && (
              <Typography variant="caption" color="text.secondary">
                {location.locationNumber && `#${location.locationNumber}`}
                {location.locationNumber && (location.city || location.state) && ' • '}
                {location.city && location.state && `${location.city}, ${location.state}`}
                {location.city && !location.state && location.city}
                {!location.city && location.state && location.state}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Status Bar */}
      <Box sx={{ px: 2, py: 1, backgroundColor: 'grey.50', display: 'flex', gap: 1 }}>
        <Chip 
          label={location.closeDate ? 'Inactive' : 'Active'} 
          size="small" 
          color={location.closeDate ? 'default' : 'success'}
          variant="outlined"
        />
        <Chip 
          label={location.ownershipType || '-'} 
          size="small" 
          variant="outlined"
        />
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label="Management" />
          <Tab label="Labor Targets" />
          <Tab label="Systems" />
          <Tab label="History" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <>
        <TabPanel value={tabValue} index={0}>
          {/* Overview Tab with Editable Sections */}
          <Box>
            <EditableSection
              title="Basic Information"
              onSave={handleSaveBasicInfo}
              loading={loading}
              onDirtyChange={handleBasicInfoDirtyChange}
            >
              <BasicInformationSection location={location} />
            </EditableSection>

            <EditableSection
              title="Organizational Assignment"
              onSave={handleSaveOrgAssignment}
              loading={loading}
              helpText="Assign this location to an organizational unit"
              onDirtyChange={handleOrgAssignmentDirtyChange}
            >
              <OrganizationalAssignmentSection 
                location={location}
                orgUnits={orgUnits}
                hierarchyDefinition={hierarchyDefinition}
              />
            </EditableSection>

            <EditableSection
              title="Operational Status"
              onSave={handleSaveOperationalStatus}
              loading={loading}
              onDirtyChange={handleOperationalStatusDirtyChange}
            >
              <OperationalStatusSection location={location} />
            </EditableSection>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Management Tab */}
          <Grid container spacing={2}>
            {/* Location Managers Section - Always show */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Location Managers
                  </Typography>
                  <IconButton 
                    size="small" 
                    sx={{ ml: 1 }}
                    onClick={() => setEditManagersModalOpen(true)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                {location.managerAssignments?.storeManagers?.length > 0 ? (
                  <List dense>
                    {location.managerAssignments.storeManagers.map((assignment, index) => (
                      <ListItem key={assignment.id || index}>
                        <ListItemIcon>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {assignment.firstName?.charAt(0) || 'M'}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              <Typography variant="body2" fontWeight="500" component="span" display="block">
                                {assignment.firstName && assignment.lastName 
                                  ? `${assignment.firstName} ${assignment.lastName}`
                                  : 'Unknown Manager'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="span" display="block">
                                {assignment.email && assignment.email}
                              </Typography>
                            </>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" component="span" display="block">
                                Effective: {assignment.effectiveDate ? format(parseISO(assignment.effectiveDate), 'MMM d, yyyy') : 'Unknown'}
                              </Typography>
                              {assignment.assignedByName && (
                                <Typography variant="caption" component="span" display="block">
                                  Assigned by: {assignment.assignedByName}
                                </Typography>
                              )}
                              {assignment.assignmentReason && (
                                <Typography variant="caption" component="span" display="block">
                                  Reason: {assignment.assignmentReason}
                                </Typography>
                              )}
                              {assignment.metadata && (
                                <Typography variant="caption" component="span" display="block" sx={{ fontStyle: 'italic' }}>
                                  Notes: {JSON.stringify(assignment.metadata)}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 1 }}>
                    <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      No location manager assigned
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* OrgLevel_1 Managers Section - Always show */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  {(() => {
                    // Get the OrgUnit name for ORGLEVEL_1 that this location belongs to
                    const orgLevel1Unit = orgUnits?.find(unit => 
                      unit.id === location.orgLevel_1
                    );
                    const orgUnitName = orgLevel1Unit?.name || location.orgLevel_1_name || '';
                    
                    // Get the label for ORGLEVEL_1 from hierarchy definition
                    const level1 = hierarchyDefinition?.find(def => 
                      def.levelId === 'ORGLEVEL_1' && def.isActive
                    );
                    const levelLabel = level1?.label || 'Level 1';
                    
                    return orgUnitName ? `${orgUnitName} ${levelLabel} Manager` : `${levelLabel} Manager`;
                  })()}
                </Typography>
                {(() => {
                  const hierarchicalManagers = location.managerAssignments?.hierarchicalManagers || {};
                  const orgLevel1Managers = hierarchicalManagers['ORGLEVEL_1'] || [];
                  
                  if (orgLevel1Managers.length > 0) {
                    return (
                      <List dense>
                        {orgLevel1Managers.map((assignment, index) => (
                          <ListItem key={assignment.id || index}>
                            <ListItemIcon>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                {assignment.firstName?.charAt(0) || 'M'}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <>
                                  <Typography variant="body2" fontWeight="500" component="span" display="block">
                                    {assignment.firstName && assignment.lastName 
                                      ? `${assignment.firstName} ${assignment.lastName}`
                                      : assignment.userName || 'Unknown Manager'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="span" display="block">
                                    {assignment.email && assignment.email}
                                  </Typography>
                                </>
                              }
                              secondary={
                                <>
                                  <Typography variant="caption" component="span" display="block">
                                    Unit: {assignment.orgUnitNameSnapshot || '-'}
                                  </Typography>
                                  <Typography variant="caption" component="span" display="block">
                                    Effective: {assignment.effectiveDate ? format(parseISO(assignment.effectiveDate), 'MMM d, yyyy') : 'Unknown'}
                                  </Typography>
                                  {assignment.assignedByName && (
                                    <Typography variant="caption" component="span" display="block">
                                      Assigned by: {assignment.assignedByName}
                                    </Typography>
                                  )}
                                  {assignment.assignmentReason && (
                                    <Typography variant="caption" component="span" display="block">
                                      Reason: {assignment.assignmentReason}
                                    </Typography>
                                  )}
                                  {assignment.metadata && (
                                    <Typography variant="caption" component="span" display="block" sx={{ fontStyle: 'italic' }}>
                                      Notes: {JSON.stringify(assignment.metadata)}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    );
                  } else {
                    const level1 = hierarchyDefinition?.find(def => 
                      def.levelId === 'ORGLEVEL_1' && def.isActive
                    );
                    const levelLabel = level1?.label?.toLowerCase() || 'level 1';
                    
                    return (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 1 }}>
                          <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          <Typography variant="body2" color="text.secondary">
                            No {levelLabel} manager assigned
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mt: -1, mb: 1 }}>
                          {levelLabel.charAt(0).toUpperCase() + levelLabel.slice(1)} managers can be assigned in the Units tab
                        </Typography>
                      </Box>
                    );
                  }
                })()}
              </Paper>
            </Grid>

            {/* Additional Hierarchical Managers (ORGLEVEL_2 and ORGLEVEL_3) if they exist */}
            {(() => {
              const hierarchicalManagers = location.managerAssignments?.hierarchicalManagers || {};
              
              // Get org level labels from hierarchy definition and org unit names
              const getLevelInfo = (levelId) => {
                const level = hierarchyDefinition?.find(def => 
                  def.levelId === levelId && def.isActive
                );
                const levelLabel = level?.label || levelId.replace('ORGLEVEL_', 'Level ');
                
                // Get the org unit name for this level
                let orgUnitName = '';
                if (levelId === 'ORGLEVEL_2') {
                  const unit = orgUnits?.find(u => u.id === location.orgLevel_2);
                  orgUnitName = unit?.name || location.orgLevel_2_name || '';
                } else if (levelId === 'ORGLEVEL_3') {
                  const unit = orgUnits?.find(u => u.id === location.orgLevel_3);
                  orgUnitName = unit?.name || location.orgLevel_3_name || '';
                }
                
                return { levelLabel, orgUnitName };
              };

              // Only show ORGLEVEL_2 and ORGLEVEL_3 if they have managers
              const additionalLevels = ['ORGLEVEL_3', 'ORGLEVEL_2'].filter(level => 
                hierarchicalManagers[level] && hierarchicalManagers[level].length > 0
              );

              return additionalLevels.map((level) => {
                const managers = hierarchicalManagers[level];
                const { levelLabel, orgUnitName } = getLevelInfo(level);

                return (
                  <Grid item xs={12} key={level}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        {orgUnitName ? `${orgUnitName} ${levelLabel} Managers` : `${levelLabel} Managers`}
                      </Typography>
                      <List dense>
                        {managers.map((assignment, index) => (
                          <ListItem key={assignment.id || index}>
                            <ListItemIcon>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                {assignment.firstName?.charAt(0) || 'M'}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <>
                                  <Typography variant="body2" fontWeight="500" component="span" display="block">
                                    {assignment.firstName && assignment.lastName 
                                      ? `${assignment.firstName} ${assignment.lastName}`
                                      : assignment.userName || 'Unknown Manager'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="span" display="block">
                                    {assignment.email && assignment.email}
                                  </Typography>
                                </>
                              }
                              secondary={
                                <>
                                  <Typography variant="caption" component="span" display="block">
                                    Unit: {assignment.orgUnitNameSnapshot || '-'}
                                  </Typography>
                                  <Typography variant="caption" component="span" display="block">
                                    Effective: {assignment.effectiveDate ? format(parseISO(assignment.effectiveDate), 'MMM d, yyyy') : 'Unknown'}
                                  </Typography>
                                  {assignment.assignedByName && (
                                    <Typography variant="caption" component="span" display="block">
                                      Assigned by: {assignment.assignedByName}
                                    </Typography>
                                  )}
                                  {assignment.assignmentReason && (
                                    <Typography variant="caption" component="span" display="block">
                                      Reason: {assignment.assignmentReason}
                                    </Typography>
                                  )}
                                  {assignment.metadata && (
                                    <Typography variant="caption" component="span" display="block" sx={{ fontStyle: 'italic' }}>
                                      Notes: {JSON.stringify(assignment.metadata)}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                );
              });
            })()}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Labor Targets Tab */}
          <LaborTargetsTab 
            location={location} 
            onUpdate={async (updatedLocation) => {
              // Update parent component
              if (onUpdate) {
                onUpdate(updatedLocation);
              }
              
              // If we're currently viewing history, refresh it
              if (tabValue === 4) {
                const locationId = location.id || location.locationId || location.ID;
                const clientId = user?.clientId || user?.client;
                
                if (locationId && clientId) {
                  setLoadingHistory(true);
                  try {
                    const history = await historyService.getLocationHistory(locationId, clientId);
                    setLocationHistory(history || []);
                  } catch (error) {
                    console.error('Error fetching location history after labor targets update:', error);
                  } finally {
                    setLoadingHistory(false);
                  }
                }
              }
              
              // Show success message
              setSnackbar({
                open: true,
                message: 'Labor targets updated successfully',
                severity: 'success'
              });
            }} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Systems Tab */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600">
                    External Systems
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={showInactiveSystems} 
                        onChange={(e) => setShowInactiveSystems(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Inactive"
                  />
                </Box>
                
                {(() => {
                  const systems = location.externalSystems || [];
                  const filteredSystems = showInactiveSystems 
                    ? systems 
                    : systems.filter(sys => sys.isActive);
                  
                  if (filteredSystems.length === 0) {
                    return (
                      <Alert severity="info" variant="outlined">
                        {systems.length === 0 
                          ? "No external systems configured for this location"
                          : showInactiveSystems 
                            ? "No systems found"
                            : "No active systems configured. Toggle 'Show Inactive' to see all systems."}
                      </Alert>
                    );
                  }
                  
                  const getSystemIcon = (systemType) => {
                    switch(systemType) {
                      case 'ALOHA_POS':
                      case 'TOAST_POS':
                        return <PointOfSaleIcon fontSize="small" />;
                      default:
                        return <ComputerIcon fontSize="small" />;
                    }
                  };
                  
                  const getSystemDisplayName = (systemType) => {
                    switch(systemType) {
                      case 'ALOHA_POS':
                        return 'Aloha POS';
                      case 'TOAST_POS':
                        return 'Toast POS';
                      default:
                        return systemType;
                    }
                  };
                  
                  const getSyncStatusIcon = (syncStatus) => {
                    switch(syncStatus) {
                      case 'ACTIVE':
                        return <CheckCircleIcon fontSize="small" color="success" />;
                      case 'SYNCING':
                        return <SyncIcon fontSize="small" color="info" />;
                      case 'ERROR':
                        return <ErrorIcon fontSize="small" color="error" />;
                      case 'DISABLED':
                        return <BlockIcon fontSize="small" color="disabled" />;
                      case 'PENDING_SETUP':
                        return <PendingIcon fontSize="small" color="warning" />;
                      default:
                        return null;
                    }
                  };
                  
                  const getSyncStatusLabel = (syncStatus) => {
                    switch(syncStatus) {
                      case 'ACTIVE':
                        return 'Active';
                      case 'SYNCING':
                        return 'Syncing';
                      case 'ERROR':
                        return 'Error';
                      case 'DISABLED':
                        return 'Disabled';
                      case 'PENDING_SETUP':
                        return 'Pending Setup';
                      default:
                        return 'Unknown';
                    }
                  };
                  
                  return (
                    <List dense>
                      {filteredSystems.map((system, index) => (
                        <ListItem 
                          key={index}
                          sx={{ 
                            opacity: system.isActive ? 1 : 0.6,
                            borderBottom: index < filteredSystems.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            pb: 1.5,
                            mb: index < filteredSystems.length - 1 ? 1.5 : 0
                          }}
                        >
                          <ListItemIcon>
                            {getSystemIcon(system.systemType)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="600">
                                  {getSystemDisplayName(system.systemType)}
                                </Typography>
                                {system.isPrimaryPOS && (
                                  <Chip label="Primary POS" size="small" color="primary" variant="outlined" />
                                )}
                                {!system.isActive && (
                                  <Chip label="Inactive" size="small" variant="outlined" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {system.systemName && (
                                  <Typography variant="caption" component="div">
                                    Name: {system.systemName}
                                  </Typography>
                                )}
                                <Typography variant="caption" component="div">
                                  System ID: {system.systemId}
                                </Typography>
                                {system.lastSyncAt ? (
                                  <Box sx={{ 
                                    mt: 0.5, 
                                    p: 0.5, 
                                    backgroundColor: (() => {
                                      if (!system.lastSyncAt) return 'error.light';
                                      try {
                                        const lastSync = parseISO(system.lastSyncAt);
                                        const hoursSinceSync = differenceInHours(new Date(), lastSync);
                                        if (hoursSinceSync < 24) return 'success.light';
                                        if (hoursSinceSync < 72) return 'warning.light';
                                        return 'error.light';
                                      } catch (e) {
                                        return 'error.light';
                                      }
                                    })(),
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: (() => {
                                      if (!system.lastSyncAt) return 'error.main';
                                      try {
                                        const lastSync = parseISO(system.lastSyncAt);
                                        const hoursSinceSync = differenceInHours(new Date(), lastSync);
                                        if (hoursSinceSync < 24) return 'success.main';
                                        if (hoursSinceSync < 72) return 'warning.main';
                                        return 'error.main';
                                      } catch (e) {
                                        return 'error.main';
                                      }
                                    })()
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <SyncIcon fontSize="small" sx={{ 
                                        color: (() => {
                                          if (!system.lastSyncAt) return 'error.dark';
                                          try {
                                            const lastSync = parseISO(system.lastSyncAt);
                                            const hoursSinceSync = differenceInHours(new Date(), lastSync);
                                            if (hoursSinceSync < 24) return 'success.dark';
                                            if (hoursSinceSync < 72) return 'warning.dark';
                                            return 'error.dark';
                                          } catch (e) {
                                            return 'error.dark';
                                          }
                                        })()
                                      }} />
                                      <Box>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                                          Last Data Import
                                        </Typography>
                                        <Typography variant="caption" component="div">
                                          {system.lastSyncAt ? format(parseISO(system.lastSyncAt), 'MMM d, yyyy h:mm a') : 'Never'}
                                        </Typography>
                                        <Typography variant="caption" component="div" sx={{ fontStyle: 'italic' }}>
                                          {system.lastSyncAt ? formatDistanceToNow(parseISO(system.lastSyncAt), { addSuffix: true }) : 'No sync data'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box sx={{ 
                                    mt: 0.5, 
                                    p: 0.5, 
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'grey.300'
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PendingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                      <Box>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                                          No Data Import Yet
                                        </Typography>
                                        <Typography variant="caption" component="div" color="text.secondary">
                                          Awaiting initial sync
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                {system.syncStatus && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    {getSyncStatusIcon(system.syncStatus)}
                                    <Typography variant="caption">
                                      Sync Status: {getSyncStatusLabel(system.syncStatus)}
                                    </Typography>
                                  </Box>
                                )}
                                {system.metadata && (
                                  <Box sx={{ mt: 0.5, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                                    <Typography variant="caption" component="div" color="text.secondary">
                                      Metadata:
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                      {typeof system.metadata === 'string' 
                                        ? system.metadata 
                                        : JSON.stringify(system.metadata, null, 2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  );
                })()}
              </Paper>
            </Grid>
            
            {/* Legacy System IDs (if they exist) */}
            {(location.posSystemId || location.laborSystemId || location.inventorySystemId) && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                    Legacy System References
                  </Typography>
                  <Typography variant="caption" color="text.secondary" paragraph>
                    These are legacy system IDs that may still be in use
                  </Typography>
                  <Grid container spacing={2}>
                    {location.posSystemId && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">POS System</Typography>
                        <Typography variant="body2">{location.posSystemId}</Typography>
                      </Grid>
                    )}
                    {location.laborSystemId && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Labor System</Typography>
                        <Typography variant="body2">{location.laborSystemId}</Typography>
                      </Grid>
                    )}
                    {location.inventorySystemId && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Inventory System</Typography>
                        <Typography variant="body2">{location.inventorySystemId}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {/* History Tab with Enhanced Search and Date Organization */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {/* Search Bar */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search by user, description, or any keyword..."
                  value={historySearchTerm}
                  onChange={(e) => setHistorySearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: historySearchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={() => setHistorySearchTerm('')}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                />
              </Box>

              {loadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : locationHistory.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No history available for this location
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <>
                  {/* Group and filter history by date */}
                  {(() => {
                    // Filter history based on search term
                    const filteredHistory = locationHistory.filter(item => {
                      if (!historySearchTerm) return true;
                      const searchLower = historySearchTerm.toLowerCase();
                      
                      // Handle description which can be string or array
                      const descriptionMatch = item.description && (
                        Array.isArray(item.description) 
                          ? item.description.some(line => line.toLowerCase().includes(searchLower))
                          : item.description.toLowerCase().includes(searchLower)
                      );
                      
                      return (
                        (item.title && item.title.toLowerCase().includes(searchLower)) ||
                        descriptionMatch ||
                        (item.changedByName && item.changedByName.toLowerCase().includes(searchLower)) ||
                        (item.changedBy && item.changedBy.toLowerCase().includes(searchLower)) ||
                        (item.changeReason && item.changeReason.toLowerCase().includes(searchLower))
                      );
                    });

                    // Group history by date and sort chronologically
                    const groupedHistory = (() => {
                      const groups = {};
                      
                      // First, sort all items by date (newest first)
                      const sortedItems = [...filteredHistory].sort((a, b) => {
                        const dateA = parseISO(a.effectiveDate || a.changedAt);
                        const dateB = parseISO(b.effectiveDate || b.changedAt);
                        return dateB - dateA; // Newest first
                      });
                      
                      // Group by date
                      sortedItems.forEach(item => {
                        const dateStr = item.effectiveDate || item.changedAt;
                        if (!dateStr) return; // Skip items without dates
                        try {
                          const date = parseISO(dateStr);
                          const dateKey = format(date, 'EEEE, MMMM d, yyyy'); // More readable date format
                          if (!groups[dateKey]) {
                            groups[dateKey] = [];
                          }
                          groups[dateKey].push(item);
                        } catch (e) {
                          console.error('Error parsing date:', dateStr, e);
                        }
                      });
                      
                      // Sort items within each date group chronologically (newest first)
                      Object.keys(groups).forEach(dateKey => {
                        groups[dateKey].sort((a, b) => {
                          const timeA = parseISO(a.effectiveDate || a.changedAt);
                          const timeB = parseISO(b.effectiveDate || b.changedAt);
                          return timeB - timeA; // Newest first within the day
                        });
                      });
                      
                      return groups;
                    })();

                    // Get icon component for history type
                    const getIconComponent = (icon) => {
                      const iconMap = {
                        'drive_file_move': DriveFileMoveIcon,
                        'person': PersonIcon,
                        'person_add': PersonIcon,
                        'schedule': ScheduleIcon,
                        'add_location': AddLocationIcon,
                        'edit': EditIcon,
                        'delete': DeleteIcon,
                        'history': HistoryIcon
                      };
                      return iconMap[icon] || HistoryIcon;
                    };

                    if (filteredHistory.length === 0) {
                      return (
                        <Paper variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                              No history entries match your search
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Try adjusting your search terms
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    }

                    return Object.entries(groupedHistory).map(([date, items], dateIndex) => {
                      // Parse the date to check if it's today or yesterday
                      let dateObj;
                      let isCurrentDay = false;
                      let isYesterdayDate = false;
                      try {
                        const dateStr = items[0].effectiveDate || items[0].changedAt;
                        if (dateStr) {
                          dateObj = parseISO(dateStr);
                          isCurrentDay = isToday(dateObj);
                          isYesterdayDate = isYesterday(dateObj);
                        }
                      } catch (e) {
                        console.error('Error parsing date for grouping:', e);
                      }
                      
                      return (
                        <Card key={date} sx={{ mb: 3, overflow: 'visible' }}>
                          <Box 
                            sx={{ 
                              p: 2.5, 
                              backgroundColor: isCurrentDay ? 'primary.lighter' : 'action.hover',
                              borderBottom: '1px solid',
                              borderBottomColor: 'divider',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon 
                                sx={{ 
                                  fontSize: 20, 
                                  color: isCurrentDay ? 'primary.main' : 'text.secondary' 
                                }} 
                              />
                              <Typography 
                                variant="subtitle1" 
                                fontWeight="600"
                                color={isCurrentDay ? 'primary.main' : 'text.primary'}
                              >
                                {isCurrentDay ? 'Today' : isYesterdayDate ? 'Yesterday' : date}
                              </Typography>
                              <Chip 
                                label={`${items.length} ${items.length === 1 ? 'change' : 'changes'}`}
                                size="small"
                                color={isCurrentDay ? 'primary' : 'default'}
                                variant={isCurrentDay ? 'filled' : 'outlined'}
                              />
                            </Box>
                          </Box>
                          <List>
                            {items.map((item, itemIndex) => {
                              const IconComponent = getIconComponent(item.icon);
                              let timeDisplay = 'Unknown';
                              try {
                                const dateStr = item.effectiveDate || item.changedAt;
                                if (dateStr) {
                                  const itemTime = parseISO(dateStr);
                                  timeDisplay = format(itemTime, 'h:mm a');
                                }
                              } catch (e) {
                                console.error('Error formatting time:', e);
                              }
                              
                              return (
                                <ListItem
                                  key={item.id}
                                  divider={itemIndex < items.length - 1}
                                  sx={{
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    borderLeft: item.isActive ? '4px solid' : 'none',
                                    borderLeftColor: item.isActive ? 'primary.main' : 'transparent',
                                    position: 'relative'
                                  }}
                                >
                                  <ListItemIcon>
                                    <IconComponent color={item.iconColor || 'inherit'} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography 
                                          variant="body2" 
                                          fontWeight="500"
                                          sx={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            flex: '1 1 auto'
                                          }}
                                        >
                                          {item.title}
                                        </Typography>
                                        {/* Time stamp */}
                                        <Typography 
                                          variant="caption" 
                                          color="text.secondary"
                                          sx={{ fontWeight: 500 }}
                                        >
                                          {timeDisplay}
                                        </Typography>
                                        {/* Show entityType chip if available */}
                                        {item.entityType && (
                                          <Chip
                                            label={item.entityType === 'LocationLaborTargets' ? 'LaborTargets' : item.entityType}
                                            size="small"
                                            variant="outlined"
                                            color={
                                              item.entityType === 'Location' ? 'primary' : 
                                              item.entityType === 'LocationLaborTargets' ? 'secondary' : 
                                              'default'
                                            }
                                            sx={{ height: 20 }}
                                          />
                                        )}
                                        {item.isActive && (
                                          <Chip
                                            label="Active"
                                            size="small"
                                            color="primary"
                                            sx={{ height: 20 }}
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box component="span">
                                        {/* Display description */}
                                        {Array.isArray(item.description) ? (
                                          <Box component="span">
                                            {item.description.map((line, idx) => (
                                              <Typography 
                                                key={idx} 
                                                variant="body2" 
                                                component="span"
                                                display="block"
                                                sx={{
                                                  wordBreak: 'break-word',
                                                  overflowWrap: 'break-word',
                                                  whiteSpace: 'pre-wrap'
                                                }}
                                              >
                                                {line}
                                              </Typography>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography 
                                            variant="body2" 
                                            component="span"
                                            display="block"
                                            sx={{
                                              wordBreak: 'break-word',
                                              overflowWrap: 'break-word',
                                              whiteSpace: 'pre-wrap'
                                            }}
                                          >
                                            {item.description}
                                          </Typography>
                                        )}
                                        
                                        {/* Changed by and reason on last line */}
                                        <Typography 
                                          variant="caption" 
                                          color="text.secondary" 
                                          component="span" 
                                          display="block"
                                          sx={{ 
                                            mt: 0.5,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word'
                                          }}
                                        >
                                          Changed by: {item.changedByName || item.changedBy || 'System'}
                                          {item.changeReason && ` • Reason: ${item.changeReason}`}
                                        </Typography>
                                      </Box>
                                    }
                                    secondaryTypographyProps={{
                                      component: 'div'
                                    }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Card>
                      );
                    });
                  })()}
                  
                  {/* Summary info */}
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {historySearchTerm ? 
                        `Showing ${locationHistory.filter(item => {
                          const searchLower = historySearchTerm.toLowerCase();
                          
                          // Handle description which can be string or array
                          const descriptionMatch = item.description && (
                            Array.isArray(item.description) 
                              ? item.description.some(line => line.toLowerCase().includes(searchLower))
                              : item.description.toLowerCase().includes(searchLower)
                          );
                          
                          return (
                            (item.title && item.title.toLowerCase().includes(searchLower)) ||
                            descriptionMatch ||
                            (item.changedByName && item.changedByName.toLowerCase().includes(searchLower)) ||
                            (item.changedBy && item.changedBy.toLowerCase().includes(searchLower)) ||
                            (item.changeReason && item.changeReason.toLowerCase().includes(searchLower))
                          );
                        }).length} of ${locationHistory.length} entries` :
                        `Total history entries: ${locationHistory.length}`
                      }
                    </Typography>
                  </Paper>
                </>
              )}
            </Grid>
          </Grid>
        </TabPanel>
          </>
        )}
      </Box>
      
      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Location Managers Modal */}
      <EditLocationManagersModal
        open={editManagersModalOpen}
        onClose={() => setEditManagersModalOpen(false)}
        location={location}
        existingManagers={location?.managerAssignments?.storeManagers || []}
        onSave={handleSaveManagers}
      />
    </Drawer>
  );
}

export default LocationDetailPanel;